//src/components/booking/index.js
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ref, get } from 'firebase/database';
import { database } from '../../firebase';
import useAuth from '../../hooks/useAuth';
import BookingStepService from './BookingStepService';
import BookingStepSchedule from './BookingStepSchedule';
import BookingStepVehicle from './BookingStepVehicle';
import BookingStepPayment from './BookingStepPayment';
import BookingStepper from './BookingStepper';
import ServiceConfirmationModal from './ServiceConfirmationModal';
import { calculateVehiclePrice } from '../../utils/vehiclePricing';

const BookingPage = ({ isMobile }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  
  // State for booking flow
  const [currentStep, setCurrentStep] = useState(0);
  const [serviceDetails, setServiceDetails] = useState(null);
  const [schedulingDetails, setSchedulingDetails] = useState({
    date: null,
    time: null
  });
  const [vehicleDetails, setVehicleDetails] = useState({
    vehicle: null,
    vehicleId: null,
    location: "",
    instructions: ""
  });
  
  // New state for confirmation modal
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [pendingService, setPendingService] = useState(null);
  const [adjustedPrice, setAdjustedPrice] = useState(0);
  const [pricingDetails, setPricingDetails] = useState(null);
  const [isLoadingVehicles, setIsLoadingVehicles] = useState(false);
  // Add flag to prevent multiple loads
  const [hasLoadedVehicle, setHasLoadedVehicle] = useState(false);
  
  // Define handlers using useCallback to prevent recreating them on every render
  const handleRemoveVehicle = useCallback(() => {
    // Clear vehicle details
    setVehicleDetails({
      vehicle: null,
      vehicleId: null,
      location: "",
      instructions: ""
    });
    
    // Reset price to base price if vehicle is removed
    if (pendingService) {
      setAdjustedPrice(pendingService.price);
      
      // Update pending service to remove adjusted price
      setPendingService(prev => {
        if (!prev) return prev;
        const updatedService = { ...prev };
        delete updatedService.adjustedPrice;
        return updatedService;
      });
      
      // Reset pricing details
      setPricingDetails({
        price: pendingService.price,
        vehicleSize: 'STANDARD',
        multiplier: 1,
        sizeDescription: 'Midsize sedans and small crossovers'
      });
    }
  }, [pendingService]);
  
  // Handler for when service is re-priced based on vehicle
// Then modify the handlePriceAdjustment function to respect standard/compact vehicles

const handlePriceAdjustment = useCallback((newPrice, priceInfo) => {
  setPricingDetails(priceInfo);
  
  // Only apply price adjustment for non-standard vehicles
  if (priceInfo && 
      priceInfo.vehicleSize !== 'STANDARD' && 
      priceInfo.vehicleSize !== 'COMPACT') {
    setAdjustedPrice(newPrice);
    
    if (serviceDetails) {
      setServiceDetails(prev => ({
        ...prev,
        adjustedPrice: newPrice
      }));
    }
    
    if (pendingService) {
      setPendingService(prev => ({
        ...prev,
        adjustedPrice: newPrice
      }));
    }
  } else {
    // For standard/compact vehicles, use original price
    const originalPrice = serviceDetails?.originalPrice || 
                         pendingService?.originalPrice || 
                         serviceDetails?.price ||
                         pendingService?.price;
    
    setAdjustedPrice(originalPrice);
    
    // Remove adjusted price property for standard vehicles
    if (serviceDetails) {
      setServiceDetails(prev => {
        const updated = { ...prev };
        delete updated.adjustedPrice;
        return updated;
      });
    }
    
    if (pendingService) {
      setPendingService(prev => {
        const updated = { ...prev };
        delete updated.adjustedPrice;
        return updated;
      });
    }
  }
}, [serviceDetails, pendingService]);
  
  // Handle confirming the service in the modal
  const handleConfirmService = useCallback((adjustedPrice) => {
    // Update the service details with potentially adjusted price
    if (adjustedPrice && pendingService && adjustedPrice !== pendingService.price) {
      setServiceDetails({
        ...pendingService,
        adjustedPrice: adjustedPrice
      });
    } else if (pendingService) {
      // Use base price (no adjustment)
      setServiceDetails(pendingService);
    }
    
    setShowConfirmationModal(false);
    setCurrentStep(1); // Move to schedule step
    window.scrollTo(0, 0);
  }, [pendingService]);
  
  // Handle canceling in the confirmation modal
  const handleCancelConfirmation = useCallback(() => {
    setShowConfirmationModal(false);
    // Go back to service selection if they cancel
    setCurrentStep(0);
    setPendingService(null);
    
    // Also clear vehicle details when canceling
    setVehicleDetails({
      vehicle: null,
      vehicleId: null,
      location: "",
      instructions: ""
    });
  }, []);
  
  // Fetch user's most recent vehicle when opening confirmation modal
  useEffect(() => {
    const loadRecentVehicle = async () => {
      // Only fetch if the user is logged in, we're showing the confirmation modal,
      // we don't already have a vehicle selected, and we haven't already loaded one
      if (user && showConfirmationModal && !vehicleDetails.vehicle && !hasLoadedVehicle) {
        try {
          setIsLoadingVehicles(true);
          setHasLoadedVehicle(true); // Mark as loaded to prevent multiple loads
          
          // Fetch user's vehicles from Firebase
          const userVehiclesRef = ref(database, `users/${user.uid}/vehicles`);
          const snapshot = await get(userVehiclesRef);
          
          if (snapshot.exists()) {
            const vehiclesData = snapshot.val();
            
            // Convert object to array and find most recently used
            const vehiclesArray = Object.keys(vehiclesData).map(key => ({
              id: key,
              ...vehiclesData[key]
            }));
            
            // Sort by lastUsed date if available
            const sortedVehicles = [...vehiclesArray].sort((a, b) => {
              if (a.lastUsed && b.lastUsed) {
                return new Date(b.lastUsed) - new Date(a.lastUsed);
              }
              return a.lastUsed ? -1 : b.lastUsed ? 1 : 0;
            });
            
            if (sortedVehicles.length > 0) {
              // Use the most recently used vehicle
              const recentVehicle = sortedVehicles[0];
              
              // Update vehicle details
              setVehicleDetails({
                ...vehicleDetails,
                vehicle: recentVehicle,
                vehicleId: recentVehicle.id
              });
              
              // Calculate price based on the vehicle
              if (pendingService) {
                const priceInfo = calculateVehiclePrice(pendingService.price, recentVehicle);
                setPricingDetails(priceInfo);
                setAdjustedPrice(priceInfo.price);
                
                // Update pending service with adjusted price
                setPendingService(prev => ({
                  ...prev,
                  adjustedPrice: priceInfo.price
                }));
              }
            }
          }
        } catch (err) {
          console.error('Error loading user vehicles:', err);
        } finally {
          setIsLoadingVehicles(false);
        }
      }
    };
    
    loadRecentVehicle();
  }, [user, showConfirmationModal, vehicleDetails.vehicle, pendingService, hasLoadedVehicle]);
  
  // Reset hasLoadedVehicle when confirmation modal closes
  useEffect(() => {
    if (!showConfirmationModal) {
      setHasLoadedVehicle(false);
    }
  }, [showConfirmationModal]);
  
  // Handle service selection from BookingStepService
  const handleServiceSelect = useCallback((service) => {
    // Store the original price as soon as we select a service
    const serviceWithOriginalPrice = {
      ...service,
      originalPrice: service.price // Preserve the original price
    };
    
    setPendingService(serviceWithOriginalPrice);
    setShowConfirmationModal(true);
  }, []);
  
  
  // Reset service selection (to choose another)
  const handleResetService = useCallback(() => {
    localStorage.removeItem('selectedService');
    setServiceDetails(null);
    setPendingService(null);
    setCurrentStep(0);
    
    // Also clear vehicle details when resetting service
    setVehicleDetails({
      vehicle: null,
      vehicleId: null,
      location: "",
      instructions: ""
    });
  }, []);
  
  useEffect(() => {
    // Check for pre-selected service from location state (used by CeramicCoatingPage)
    if (location.state?.preSelectedService) {
      // If we have a specific service selected, we'll handle it in BookingStepService
      return;
    }
    
    // Parse URL parameters when the component mounts
    const queryParams = new URLSearchParams(location.search);
    const serviceId = queryParams.get('service');
    const serviceTitle = queryParams.get('title');
    const servicePrice = queryParams.get('price');
    const serviceCategory = queryParams.get('category');
    
    // If we have URL parameters for a service, set the service details
    if (serviceId && serviceTitle && servicePrice) {
      const selectedService = {
        id: serviceId,
        title: serviceTitle,
        price: parseFloat(servicePrice),
        category: serviceCategory || ''
      };
      
      setPendingService(selectedService);
      setShowConfirmationModal(true);
      
      // Save the selected service to localStorage
      localStorage.setItem('selectedService', JSON.stringify(selectedService));
    } else {
      // Check for a saved service in localStorage
      const savedService = localStorage.getItem('selectedService');
      if (savedService) {
        try {
          const parsedService = JSON.parse(savedService);
          setServiceDetails(parsedService);
          
          // Check if we should show confirmation modal
          // Only show it if we're just starting (not resuming a booking)
          const pendingBooking = localStorage.getItem('pendingBooking');
          if (!pendingBooking) {
            setPendingService(parsedService);
            setShowConfirmationModal(true);
          }
          
        } catch (e) {
          localStorage.removeItem('selectedService');
        }
      }
      
      // Check for a pending booking session
      const pendingBooking = localStorage.getItem('pendingBooking');
      if (pendingBooking) {
        try {
          const bookingData = JSON.parse(pendingBooking);
          
          // Restore all booking data
          if (bookingData.serviceDetails) {
            setServiceDetails(bookingData.serviceDetails);
          }
          
          if (bookingData.schedulingDetails) {
            // Convert date string back to Date object
            const schedulingData = {...bookingData.schedulingDetails};
            if (schedulingData.date) {
              schedulingData.date = new Date(schedulingData.date);
            }
            setSchedulingDetails(schedulingData);
          }
          
          if (bookingData.vehicleDetails) {
            setVehicleDetails(bookingData.vehicleDetails);
            
            // Recalculate price based on vehicle if available
            if (bookingData.serviceDetails && bookingData.vehicleDetails.vehicle) {
              const priceInfo = calculateVehiclePrice(
                bookingData.serviceDetails.price,
                bookingData.vehicleDetails.vehicle
              );
              
              setPricingDetails(priceInfo);
              setAdjustedPrice(priceInfo.price);
            }
          }
          
          // Set current step based on the bookmark
          if (bookingData.returnToVehicleStep) {
            setCurrentStep(2); // Vehicle step
          } else if (typeof bookingData.currentStep === 'number') {
            setCurrentStep(bookingData.currentStep);
          } else if (savedService) {
            setCurrentStep(1); // Default to the schedule step if we have service but no specific step
          }
          
          // Clear the pending booking
          localStorage.removeItem('pendingBooking');
        } catch (e) {
          console.error('Error restoring booking state:', e);
          localStorage.removeItem('pendingBooking');
        }
      } else if (savedService && !showConfirmationModal) {
        // Only set step to 1 if there's a saved service but no pending booking
        // and we're not showing the confirmation modal
        setCurrentStep(1);
      }
    }
  }, [location]);
  
  // Update price when service or vehicle changes
  useEffect(() => {
    if (serviceDetails) {
      // If we have a vehicle, calculate adjusted price
      if (vehicleDetails?.vehicle) {
        const { price } = calculateVehiclePrice(serviceDetails.price, vehicleDetails.vehicle);
        setAdjustedPrice(price);
        
        // Update service details with new price
        setServiceDetails(prev => ({
          ...prev,
          adjustedPrice: price
        }));
      } else {
        // No vehicle selected yet, use base price
        setAdjustedPrice(serviceDetails.price);
        
        // Update service details to remove adjustedPrice
        setServiceDetails(prev => {
          const updatedService = { ...prev };
          delete updatedService.adjustedPrice;
          return updatedService;
        });
      }
      
      // Save to localStorage
      localStorage.setItem('selectedService', JSON.stringify(serviceDetails));
    }
  }, [serviceDetails?.price, vehicleDetails.vehicle]);
  
  // Handle next step navigation
// Handle next step navigation
const handleNextStep = useCallback(() => {
  // Save the current step to localStorage for resume capabilities
  if (serviceDetails && schedulingDetails.date && schedulingDetails.time && currentStep === 1) {
    // Save scheduling data before proceeding
    const pendingBooking = {
      serviceDetails: {
        ...serviceDetails,
        originalPrice: serviceDetails.price, // Always preserve original price
        ...(serviceDetails.adjustedPrice ? { adjustedPrice: serviceDetails.adjustedPrice } : {})
      },
      schedulingDetails: {
        ...schedulingDetails,
        date: schedulingDetails.date.toISOString() // Convert Date to string for storage
      },
      vehicleDetails,
      currentStep: currentStep + 1
    };
    localStorage.setItem('pendingBooking', JSON.stringify(pendingBooking));
  }
  
  console.log(`Moving from step ${currentStep} to step ${currentStep + 1}`);
  
  // Update the current step
  setCurrentStep(prevStep => prevStep + 1);
  
  // Scroll to top
  window.scrollTo(0, 0);
}, [currentStep, serviceDetails, schedulingDetails, vehicleDetails]);
  
  // Handle back navigation
  const handleBack = useCallback(() => {
    setCurrentStep(currentStep - 1);
    window.scrollTo(0, 0);
  }, [currentStep]);

  // Handle successful booking completion
  const handleBookingSuccess = useCallback(() => {
    // Clear any stored booking data
    localStorage.removeItem('selectedService');
    localStorage.removeItem('pendingBooking');
    
    // Navigate to success page
    navigate('/booking-success');
  }, [navigate]);
  
  // Get display price based on adjusted price if available
  const getDisplayPrice = useCallback(() => {
    if (!serviceDetails) return null;
    
    // If it's a standard/compact vehicle, use the original price
    if (pricingDetails && 
        (pricingDetails.vehicleSize === 'STANDARD' || 
         pricingDetails.vehicleSize === 'COMPACT')) {
      return serviceDetails.originalPrice || serviceDetails.price;
    }
    
    // Otherwise use the adjusted price if available
    return serviceDetails.adjustedPrice || serviceDetails.price;
  }, [serviceDetails, pricingDetails]);
  
  // Get page title based on current step
  const getPageTitle = useCallback(() => {
    switch (currentStep) {
      case 0: return "Select Service";
      case 1: return "Schedule Appointment";
      case 2: return "Vehicle Information";
      case 3: return "Review & Pay";
      default: return "Book Your Detail";
    }
  }, [currentStep]);
  
  // Styling classes based on mobile/desktop
  const containerClass = isMobile 
    ? "px-4 py-6" 
    : "container mx-auto px-6 py-12";

  return (
    <div className={`min-h-screen bg-gray-50 pt-20 ${containerClass}`}>
      <div className="max-w-6xl mx-auto">
        {/* Page Header */}
        <div className="mb-6 md:mb-8 text-center md:text-left">
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">{getPageTitle()}</h1>
          <p className="text-gray-600">Book your premium mobile detailing service</p>
        </div>
        
        {/* Service Info - Only displayed above stepper when service is confirmed */}
        {serviceDetails && !showConfirmationModal && (
          <div className="text-center mb-8">
            <h2 className="text-3xl font-light tracking-tight text-gray-900 mb-1">{serviceDetails.title}</h2>
            <p className="text-2xl font-medium text-blue-600">${getDisplayPrice()}</p>
            {serviceDetails.adjustedPrice && serviceDetails.adjustedPrice !== serviceDetails.price && (
              <p className="text-sm text-gray-500">
                *Price adjusted based on your vehicle size
              </p>
            )}
          </div>
        )}
        
        {/* Progress Stepper */}
        <BookingStepper 
          currentStep={currentStep} 
          isMobile={isMobile}
        />
        
        {currentStep === 0 && (
  <BookingStepService 
    serviceDetails={serviceDetails}
    setServiceDetails={handleServiceSelect}
    onNextStep={handleNextStep}
    onResetService={handleResetService}
    onPriceChange={handlePriceAdjustment}
  />
)}

{currentStep === 1 && (
  <BookingStepSchedule 
    serviceDetails={{
      ...serviceDetails,
      price: getDisplayPrice()
    }}
    schedulingDetails={schedulingDetails}
    setSchedulingDetails={setSchedulingDetails}
    onNextStep={handleNextStep}
    onBack={handleBack}
    onChangeService={() => setCurrentStep(0)}
  />
)}

{currentStep === 2 && (
  <BookingStepVehicle 
    serviceDetails={{
      ...serviceDetails,
      originalPrice: serviceDetails.price, // Always pass original price
      price: getDisplayPrice()
    }}
    schedulingDetails={schedulingDetails}
    vehicleDetails={vehicleDetails}
    setVehicleDetails={setVehicleDetails}
    onNextStep={handleNextStep} // This is critical - ensure this is passed properly
    onBack={handleBack}
    onChangeService={() => setCurrentStep(0)}
    onChangeSchedule={() => setCurrentStep(1)}
    onPriceChange={handlePriceAdjustment}
  />
)}

{currentStep === 3 && (
  <BookingStepPayment 
    serviceDetails={{
      ...serviceDetails,
      originalPrice: serviceDetails.price, // Always pass original price
      price: getDisplayPrice()
    }}
    schedulingDetails={schedulingDetails}
    vehicleDetails={vehicleDetails}
    pricingDetails={pricingDetails}
    onBack={handleBack}
    onSuccess={handleBookingSuccess}
    onChangeService={() => setCurrentStep(0)}
    onChangeSchedule={() => setCurrentStep(1)}
    onChangeVehicle={() => setCurrentStep(2)}
  />
)}
        
     {/* Service Confirmation Modal */}
{showConfirmationModal && pendingService && (
  <ServiceConfirmationModal 
    serviceDetails={pendingService}
    vehicleDetails={vehicleDetails}
    onConfirm={handleConfirmService}
    onCancel={handleCancelConfirmation}
    onChangeService={handleResetService}
    onRemoveVehicle={handleRemoveVehicle}
    isLoadingVehicle={isLoadingVehicles}
    onPriceChange={handlePriceAdjustment}
  />
)}
      </div>
    </div>
  );
};

export default BookingPage;